import { captureError } from './sentry';
import { trackEvent } from './analytics';
import { measurePerformance } from '../performance';

interface LogEntry {
  level: 'debug' | 'info' | 'warn' | 'error';
  message: string;
  timestamp: string;
  context?: Record<string, any>;
}

interface PerformanceEntry {
  name: string;
  duration: number;
  timestamp: string;
  context?: Record<string, any>;
}

class MonitoringService {
  private static instance: MonitoringService;
  private logs: LogEntry[] = [];
  private metrics: PerformanceEntry[] = [];
  private maxEntries = 1000;
  private vitalsData: Record<string, number> = {};
  private errorListeners: ((error: LogEntry) => void)[] = [];
  private performanceListeners: ((metric: PerformanceEntry) => void)[] = [];

  private constructor() {
    this.setupGlobalHandlers();
    this.setupPerformanceMonitoring();
  }

  static getInstance(): MonitoringService {
    if (!MonitoringService.instance) {
      MonitoringService.instance = new MonitoringService();
    }
    return MonitoringService.instance;
  }

  private setupGlobalHandlers() {
    window.addEventListener('unhandledrejection', (event) => {
      this.logError('Unhandled Promise Rejection', {
        reason: event.reason,
        stack: event.reason?.stack,
      });
      captureError(event.reason);
    });

    window.addEventListener('error', (event) => {
      this.logError('Uncaught Error', {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        error: event.error,
      });
      captureError(event.error);
    });
  }

  private setupPerformanceMonitoring() {
    if ('PerformanceObserver' in window) {
      // Monitor Core Web Vitals
      const observer = new PerformanceObserver((list) => {
        list.getEntries().forEach((entry) => {
          const metric = entry as PerformanceEntry;
          this.vitalsData[metric.name] = metric.duration;
          
          if (metric.name === 'LCP' && metric.duration > 2500) {
            this.logWarning('Poor LCP detected', { value: metric.duration });
            trackEvent('poor_web_vital', { metric: 'LCP', value: metric.duration });
          }
          if (metric.name === 'FID' && metric.duration > 100) {
            this.logWarning('Poor FID detected', { value: metric.duration });
            trackEvent('poor_web_vital', { metric: 'FID', value: metric.duration });
          }
          if (metric.name === 'CLS' && metric.duration > 0.1) {
            this.logWarning('Poor CLS detected', { value: metric.duration });
            trackEvent('poor_web_vital', { metric: 'CLS', value: metric.duration });
          }
        });
      });

      observer.observe({ entryTypes: ['largest-contentful-paint', 'first-input', 'layout-shift'] });
    }

    // Monitor network requests
    if ('PerformanceObserver' in window) {
      const observer = new PerformanceObserver((list) => {
        list.getEntries().forEach((entry) => {
          const request = entry as PerformanceResourceTiming;
          if (request.duration > 1000) {
            this.logWarning('Slow network request', {
              url: request.name,
              duration: request.duration,
              type: request.initiatorType,
            });
            trackEvent('slow_network_request', {
              url: request.name,
              duration: request.duration,
              type: request.initiatorType,
            });
          }
        });
      });

      observer.observe({ entryTypes: ['resource'] });
    }

    // Monitor memory usage
    if ('memory' in performance) {
      setInterval(() => {
        const memory = (performance as any).memory;
        if (memory.usedJSHeapSize > memory.jsHeapSizeLimit * 0.9) {
          this.logWarning('High memory usage', {
            used: memory.usedJSHeapSize,
            limit: memory.jsHeapSizeLimit,
          });
          trackEvent('high_memory_usage', {
            used: memory.usedJSHeapSize,
            limit: memory.jsHeapSizeLimit,
          });
        }
      }, 30000);
    }
  }

  logError(message: string, context?: Record<string, any>) {
    const entry = this.createLogEntry('error', message, context);
    this.logs.push(entry);
    this.notifyErrorListeners(entry);
    this.trimLogs();
    captureError(new Error(message), context);
  }

  logWarning(message: string, context?: Record<string, any>) {
    const entry = this.createLogEntry('warn', message, context);
    this.logs.push(entry);
    this.trimLogs();
  }

  logInfo(message: string, context?: Record<string, any>) {
    const entry = this.createLogEntry('info', message, context);
    this.logs.push(entry);
    this.trimLogs();
  }

  logDebug(message: string, context?: Record<string, any>) {
    const entry = this.createLogEntry('debug', message, context);
    this.logs.push(entry);
    this.trimLogs();
  }

  private createLogEntry(level: LogEntry['level'], message: string, context?: Record<string, any>): LogEntry {
    return {
      level,
      message,
      timestamp: new Date().toISOString(),
      context,
    };
  }

  trackPerformance(name: string, duration: number, context?: Record<string, any>) {
    const entry: PerformanceEntry = {
      name,
      duration,
      timestamp: new Date().toISOString(),
      context,
    };

    this.metrics.push(entry);
    this.notifyPerformanceListeners(entry);
    this.trimMetrics();

    measurePerformance(name, duration);

    if (duration > 1000) {
      this.logWarning(`Slow performance detected: ${name}`, { duration, ...context });
      trackEvent('slow_performance', { component: name, duration, ...context });
    }
  }

  addErrorListener(listener: (error: LogEntry) => void) {
    this.errorListeners.push(listener);
    return () => {
      this.errorListeners = this.errorListeners.filter(l => l !== listener);
    };
  }

  addPerformanceListener(listener: (metric: PerformanceEntry) => void) {
    this.performanceListeners.push(listener);
    return () => {
      this.performanceListeners = this.performanceListeners.filter(l => l !== listener);
    };
  }

  private notifyErrorListeners(error: LogEntry) {
    this.errorListeners.forEach(listener => listener(error));
  }

  private notifyPerformanceListeners(metric: PerformanceEntry) {
    this.performanceListeners.forEach(listener => listener(metric));
  }

  private trimLogs() {
    if (this.logs.length > this.maxEntries) {
      this.logs = this.logs.slice(-this.maxEntries);
    }
  }

  private trimMetrics() {
    if (this.metrics.length > this.maxEntries) {
      this.metrics = this.metrics.slice(-this.maxEntries);
    }
  }

  getLogs(level?: LogEntry['level']): LogEntry[] {
    return level ? this.logs.filter(log => log.level === level) : this.logs;
  }

  getMetrics(): PerformanceEntry[] {
    return this.metrics;
  }

  getWebVitals(): Record<string, number> {
    return { ...this.vitalsData };
  }

  clearLogs() {
    this.logs = [];
  }

  clearMetrics() {
    this.metrics = [];
  }
}

export const monitor = MonitoringService.getInstance();